import { Widget } from "@/utils/widgets";
import { create } from "zustand";

export interface PopoutWidget extends Widget {
  widgetCompositeId?: string;
}

/**
 * Identity of a popped-out widget.
 *
 * Two instances of the same widget on a dashboard share `widget.id` (the type),
 * so keying popouts on that made them indistinguishable: the second instance
 * could never be popped out, and the popout button lit up on both. The composite
 * id is per-instance, so use it whenever we have one. Widgets rendered outside a
 * tab have no composite id and fall back to the type id.
 */
export const popoutKey = (widget: PopoutWidget): string =>
  widget.widgetCompositeId ?? widget.id;

interface PopoutWidgetsStoreState {
  widgets: PopoutWidget[];

  addWidget: (widget: PopoutWidget) => void;
  removeWidget: (key: string) => void;
  toggleWidget: (key: string) => void;
  updateWidget: (key: string, updates: Partial<PopoutWidget>) => void;
}

export const usePopoutWidgetsStore = create<PopoutWidgetsStoreState>((set) => ({
  widgets: [],

  // Adds a new widget if that instance is not already popped out
  addWidget: (widget) =>
    set((state) => {
      const exists = state.widgets.some(
        (w) => popoutKey(w) === popoutKey(widget)
      );
      if (exists) {
        return state; // Do not add duplicate
      }
      return {
        widgets: [...state.widgets, widget],
      };
    }),

  // Removes a widget by its instance key
  removeWidget: (key) =>
    set((state) => ({
      widgets: state.widgets.filter((w) => popoutKey(w) !== key),
    })),

  // Toggles a widget: removes it if it exists, no-op otherwise
  toggleWidget: (key) =>
    set((state) => {
      const exists = state.widgets.some((w) => popoutKey(w) === key);
      return exists
        ? { widgets: state.widgets.filter((w) => popoutKey(w) !== key) }
        : state;
    }),

  // Updates a widget by its instance key
  updateWidget: (key, updates) =>
    set((state) => ({
      widgets: state.widgets.map((w) =>
        popoutKey(w) === key ? { ...w, ...updates } : w
      ),
    })),
}));
